import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	OnDestroy,
	OnInit,
	Output,
	ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import {
	AppDataService,
	GeoZoneService,
	GoogleFleetService,
	XpressUserService,
	OperatorDashboardService
} from 'app/services';
import * as moment from 'moment/moment';
import {
	GetOperatorRevenueRequest,
	GetOperatorRevenueResponse,
	DriverInformation,
	GetOperatorOnlineDriversRequest,
	GetOperatorOnlineDriversResponse,
	CancelResultTypeModel,
	GetOperatorDashboardStatisticsRequest,
	GetOperatorDashboardStatisticsResponse,
	OperatorInfoCard,
	XpressUserProfile,
	DriverActivityStatus
} from 'app/models';
import { XpressCalendarFilter } from 'app/components/xpress-calendar/xpress-calendar-filter';
import { Subscription } from 'rxjs';
import { OperatorLiveMapMarkersService } from './operator-live-map-markers.service';
import { DashboardDatePreset, DashboardDateSelection } from './operator-dashboard-date-picker.component';
import { OperatorDashboardTourService } from './operator-dashboard-tour.service';

interface DashboardTourStep {
	id: 'date-range' | 'performance' | 'bookings' | 'map' | 'drivers';
	title: string;
	body: string;
	allowsPreview: boolean;
}

export enum OperatorHomeMenuEnum {
	Dashboard = 1
}

export type { DashboardDatePreset } from './operator-dashboard-date-picker.component';

export interface GetUserProfileDTO {
	XpressUserId: number;
}

@Component({
	selector: 'operator-dashboard',
	templateUrl: './operator-dashboard.component.html',
	styleUrls: ['./operator-dashboard.component.scss'],
	providers: [OperatorLiveMapMarkersService]
})
export class OperatorDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('operatorMap') mapElement?: ElementRef<HTMLElement>;
	@ViewChild('tourStartButton') tourStartButton?: ElementRef<HTMLButtonElement>;

	readonly kpiSkeletonItems = [1, 2, 3];
	readonly bookingHistoryEmptyLabels = ['Total Requests', 'Completed Bookings', 'Active Bookings'];
	readonly tourSteps: DashboardTourStep[] = [
		{
			id: 'date-range',
			title: 'Choose a date range',
			body: 'This filter controls the period used by your performance metrics and booking activity. Empty messages refer to the selected date range.',
			allowsPreview: false
		},
		{
			id: 'performance',
			title: 'Understand your performance',
			body: 'Your rates are unavailable because there is no eligible booking activity for the selected date range.',
			allowsPreview: true
		},
		{
			id: 'bookings',
			title: 'Track booking activity',
			body: 'There are no booking requests to summarize for the selected date range yet.',
			allowsPreview: true
		},
		{
			id: 'map',
			title: 'See current driver locations',
			body: 'The live map has no markers because none of your drivers are online right now. It is independent of the date filter.',
			allowsPreview: true
		},
		{
			id: 'drivers',
			title: 'Monitor online drivers',
			body: 'No drivers are online right now. When a driver goes online, their current status and latest location will appear here.',
			allowsPreview: true
		}
	];
	readonly sampleKpiCards: OperatorInfoCard[] = [
		{
			Header: 'Completion Rate',
			HeaderPercentage: 88,
			PercentageIncrease: -66,
			Footer: '15 out of 17',
			ChartLabels: ['1', '2', '3', '4', '5', '6', '7'],
			LabelsData: [32, 58, 40, 74, 46, 88, 52]
		},
		{
			Header: 'Acceptance Rate',
			HeaderPercentage: 94,
			PercentageIncrease: -66,
			Footer: '17 out of 18',
			ChartLabels: ['1', '2', '3', '4', '5', '6', '7'],
			LabelsData: [40, 72, 48, 90, 55, 84, 62]
		},
		{
			Header: 'Cancellation Rate',
			HeaderPercentage: 12,
			PercentageIncrease: 4,
			Footer: '2 out of 17',
			ChartLabels: ['1', '2', '3', '4', '5', '6', '7'],
			LabelsData: [70, 64, 40, 22, 18, 14, 12]
		}
	];
	readonly sampleBookingCards: OperatorInfoCard[] = [
		{ Header: 'Total Requests', HeaderPercentage: 18, PercentageIncrease: 0, Footer: '', ChartLabels: [], LabelsData: [] },
		{ Header: 'Completed Bookings', HeaderPercentage: 15, PercentageIncrease: 0, Footer: '', ChartLabels: [], LabelsData: [] },
		{ Header: 'Active Bookings', HeaderPercentage: 0, PercentageIncrease: 0, Footer: '', ChartLabels: [], LabelsData: [] }
	];
	readonly sampleBookingChart = {
		labels: ['Selected Period'],
		datasets: [
			{ label: 'Total Requests', data: [18], backgroundColor: '#FFC043', borderRadius: 4 },
			{ label: 'Completed Bookings', data: [15], backgroundColor: '#0A84FF', borderRadius: 4 },
			{ label: 'Active Bookings', data: [0], backgroundColor: '#F77F00', borderRadius: 4 }
		]
	};
	readonly sampleOnlineDrivers: DriverInformation[] = [
		{
			DriverId: -1,
			FirstName: 'John',
			LastName: 'Doe',
			PhoneNumber: '+63 917 832 4561',
			ImageUrl: '',
			Lat: 14.6091,
			Long: 121.0223,
			DriverActivityStatus: DriverActivityStatus.Idle
		},
		{
			DriverId: -2,
			FirstName: 'Joseph',
			LastName: 'Sanchez',
			PhoneNumber: '+63 917 832 4561',
			ImageUrl: '',
			Lat: 14.5902,
			Long: 121.0018,
			DriverActivityStatus: DriverActivityStatus.Idle
		}
	];

	tourPhase: 'idle' | 'welcome' | 'steps' = 'idle';
	tourStepIndex = 0;
	tourPreviewFilled = false;
	private tourSubscription?: Subscription;
	readonly defaultKpiCards: OperatorInfoCard[] = [
		{ Header: 'Completion Rate', HeaderPercentage: 0, PercentageIncrease: 0, Footer: '0 out of 0', ChartLabels: [], LabelsData: [] },
		{ Header: 'Acceptance Rate', HeaderPercentage: 0, PercentageIncrease: 0, Footer: '0 out of 0', ChartLabels: [], LabelsData: [] },
		{ Header: 'Cancellation Rate', HeaderPercentage: 0, PercentageIncrease: 0, Footer: '0 out of 0', ChartLabels: [], LabelsData: [] }
	];

	/** Default viewport (Metro Manila) — same as Ops Tower Live Map. */
	private static readonly METRO_MANILA_CENTER: google.maps.LatLngLiteral = { lat: 14.6091, lng: 121.0223 };
	private static readonly METRO_MANILA_DEFAULT_ZOOM = 13;
	private static readonly GOOGLE_MAPS_LOAD_TIMEOUT_MS = 20_000;
	private static readonly FLEET_ENGINE_PROJECT_ID = 'xpress-366609';

	private viewReady = false;
	private componentDestroyed = false;
	private markerClickSubscription?: Subscription;
	private geoZoneSubscription?: Subscription;
	private zoomControlAdded = false;
	private mapStartGeneration = 0;
	private mapsLoadTimeoutId: number | null = null;
	private locationProvider: google.maps.journeySharing.FleetEngineTripLocationProvider | null = null;
	private activeGoogleTripId = '';

	public map!: google.maps.Map;
	lineColor!: string;
	isRevenueLoading = false;
	isStatisticsLoading = false;
	isOnlineDriverLoading = false;
	isOnlineDriverError = false;
	profile: XpressUserProfile = new XpressUserProfile();

	/** Privacy-safe marker card (Idle / On Trip only). */
	selectedMapDriver: DriverInformation | null = null;

	constructor(
		private geoZoneService: GeoZoneService,
		private operatorDashboardService: OperatorDashboardService,
		private messageService: MessageService,
		private userService: XpressUserService,
		private appData: AppDataService,
		private router: Router,
		private liveMapMarkers: OperatorLiveMapMarkersService,
		private googleFleetService: GoogleFleetService,
		private dashboardTour: OperatorDashboardTourService
	) {
		const dto: GetUserProfileDTO = {
			XpressUserId: +this.appData.authData.UserId
		};

		userService.getUserProfile(dto, this.messageService).then(profile => {
			if (profile) {
				this.profile = profile;
			}
		});
	}

	public selectGeoZone: any;

	revenueData: any;

	requestsOptions: any;
	requestsData: any;

	data: any;
	dataCancel!: CancelResultTypeModel;
	cancelVal: number[] = [28, 48, 40, 19, 86, 27, 90];

	options: any;

	revenue: GetOperatorRevenueResponse | any;
	onlineDrivers: GetOperatorOnlineDriversResponse = new GetOperatorOnlineDriversResponse();
	statistics: GetOperatorDashboardStatisticsResponse | any;

	filterObj: XpressCalendarFilter = {
		startDate: moment(new Date()).format('YYYY-MM-DD'),
		endDate: moment(new Date()).format('YYYY-MM-DD')
	};
	datePreset: DashboardDatePreset = 'today';

	@Output() public OnViewDriverClick = new EventEmitter();

	get greetingName(): string {
		const first = (this.profile.FirstName || '').trim();
		const last = (this.profile.LastName || '').trim();
		if (!first) {
			return 'Operator';
		}
		return last ? `${first} ${last.charAt(0).toUpperCase()}.` : first;
	}

	get visibleKpiCards(): OperatorInfoCard[] {
		const cards = this.statistics?.OperatorInfoCards;
		return cards?.length ? cards : this.defaultKpiCards;
	}

	get activeTourStep(): DashboardTourStep | null {
		if (this.tourPhase !== 'steps') {
			return null;
		}
		return this.tourSteps[this.tourStepIndex] ?? null;
	}

	get isLastTourStep(): boolean {
		return this.tourStepIndex >= this.tourSteps.length - 1;
	}

	get performancePreviewActive(): boolean {
		return this.activeTourStep?.id === 'performance';
	}

	get bookingPreviewActive(): boolean {
		return this.activeTourStep?.id === 'bookings';
	}

	get mapPreviewActive(): boolean {
		return this.activeTourStep?.id === 'map';
	}

	get driversPreviewActive(): boolean {
		return this.activeTourStep?.id === 'drivers';
	}

	get displayedOnlineDrivers(): DriverInformation[] {
		if (this.driversPreviewActive) {
			return this.tourPreviewFilled ? this.sampleOnlineDrivers : [];
		}
		return this.onlineDrivers?.Drivers ?? [];
	}

	get showOnlineDriversEmpty(): boolean {
		if (this.driversPreviewActive) {
			return !this.tourPreviewFilled;
		}
		return !this.onlineDrivers?.Drivers?.length && !this.isOnlineDriverLoading && !this.isOnlineDriverError;
	}

	get showKpiCards(): boolean {
		if (this.performancePreviewActive) {
			return true;
		}
		return !this.isStatisticsLoading && !!this.statistics?.Success;
	}

	get kpiCards(): OperatorInfoCard[] {
		if (!this.performancePreviewActive) {
			return this.visibleKpiCards;
		}
		if (!this.tourPreviewFilled) {
			return this.defaultKpiCards;
		}
		return this.sampleKpiCards;
	}

	get showBookingFilled(): boolean {
		if (this.bookingPreviewActive) {
			return this.tourPreviewFilled;
		}
		return this.hasBookingActivity() && !!this.statistics?.Success;
	}

	get showBookingEmpty(): boolean {
		if (this.bookingPreviewActive) {
			return !this.tourPreviewFilled;
		}
		return !this.hasBookingActivity() && !!this.statistics?.Success;
	}

	get bookingSummaryCards(): OperatorInfoCard[] {
		if (this.bookingPreviewActive && this.tourPreviewFilled) {
			return this.sampleBookingCards;
		}
		return this.statistics?.LongChart?.OperatorInfoCards ?? [];
	}

	get bookingChartData(): any {
		if (this.bookingPreviewActive && this.tourPreviewFilled) {
			return this.sampleBookingChart;
		}
		return this.requestsData ?? { labels: [], datasets: [] };
	}

	private get tourUserId(): string {
		return this.appData.authData?.UserId || '0';
	}

	private get isOperatorPortal(): boolean {
		return this.router.url.includes('/operator/dashboard');
	}

	openWelcome(): void {
		this.tourPhase = 'welcome';
		this.tourStepIndex = 0;
		this.tourPreviewFilled = false;
		window.setTimeout(() => this.tourStartButton?.nativeElement.focus());
	}

	startTour(): void {
		this.tourStepIndex = 0;
		this.tourPreviewFilled = false;
		this.tourPhase = 'steps';
		this.syncMapWithTour();
		this.scrollSpotlightIntoView();
	}

	exploreOnMyOwn(): void {
		this.dashboardTour.suppress(this.tourUserId);
		this.closeTour();
	}

	skipTour(): void {
		this.dashboardTour.suppress(this.tourUserId);
		this.closeTour();
	}

	nextTourStep(): void {
		if (this.isLastTourStep) {
			this.dashboardTour.complete(this.tourUserId);
			this.closeTour();
			return;
		}
		this.tourStepIndex += 1;
		this.tourPreviewFilled = false;
		this.syncMapWithTour();
		this.scrollSpotlightIntoView();
	}

	previousTourStep(): void {
		if (this.tourStepIndex === 0) {
			return;
		}
		this.tourStepIndex -= 1;
		this.tourPreviewFilled = false;
		this.syncMapWithTour();
		this.scrollSpotlightIntoView();
	}

	toggleTourPreview(): void {
		this.tourPreviewFilled = !this.tourPreviewFilled;
		if (this.mapPreviewActive) {
			this.syncMapWithTour();
		}
	}

	private closeTour(): void {
		this.tourPhase = 'idle';
		this.tourStepIndex = 0;
		this.tourPreviewFilled = false;
		this.syncMapWithTour();
	}

	private syncMapWithTour(): void {
		if (!this.map) {
			return;
		}
		if (this.mapPreviewActive) {
			this.applyMapTourMarkers();
			return;
		}
		this.plotOnlineDriversOnMap();
	}

	private applyMapTourMarkers(): void {
		if (!this.map) {
			return;
		}
		this.closeMarkerCard();
		this.liveMapMarkers.setDrivers(
			this.tourPreviewFilled ? this.sampleOnlineDrivers : [],
			this.map,
			{ hiddenDriverId: null }
		);
		if (this.tourPreviewFilled) {
			this.fitMapToDriverMarkers();
		}
	}

	private scrollSpotlightIntoView(): void {
		window.setTimeout(() => {
			document.querySelector('.tour-spotlight')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		});
	}

	private clearMapsLoadTimeout(): void {
		if (this.mapsLoadTimeoutId != null) {
			window.clearTimeout(this.mapsLoadTimeoutId);
			this.mapsLoadTimeoutId = null;
		}
	}

	private waitForGoogleMapsLoaded(
		timeoutMs: number = OperatorDashboardComponent.GOOGLE_MAPS_LOAD_TIMEOUT_MS
	): Promise<typeof google> {
		const startTimeMs = Date.now();
		this.clearMapsLoadTimeout();

		return new Promise((resolve, reject) => {
			const check = (): void => {
				this.mapsLoadTimeoutId = null;

				if (this.componentDestroyed) {
					reject(new Error('Component destroyed'));
					return;
				}

				const g = (window as unknown as { google?: typeof google }).google;
				if (g?.maps?.Map) {
					resolve(g);
					return;
				}

				if (Date.now() - startTimeMs > timeoutMs) {
					reject(new Error('Google Maps API not loaded'));
					return;
				}

				this.mapsLoadTimeoutId = window.setTimeout(check, 100);
			};

			check();
		});
	}

	private getDefaultMapOptions(): google.maps.MapOptions {
		return {
			center: OperatorDashboardComponent.METRO_MANILA_CENTER,
			zoom: OperatorDashboardComponent.METRO_MANILA_DEFAULT_ZOOM,
			mapTypeControl: false,
			fullscreenControl: false,
			streetViewControl: false,
			zoomControl: false,
			disableDefaultUI: true
		};
	}

	public async startMap(): Promise<void> {
		const mapEl = this.mapElement?.nativeElement;
		// Operator portal does not set a geo zone; map uses a fixed Metro Manila viewport.
		if (!mapEl || this.componentDestroyed) {
			return;
		}

		if (this.map) {
			this.plotOnlineDriversOnMap();
			return;
		}

		const startGeneration = ++this.mapStartGeneration;

		try {
			const g = await this.waitForGoogleMapsLoaded();
			if (this.componentDestroyed || startGeneration !== this.mapStartGeneration || this.map) {
				return;
			}

			this.liveMapMarkers.clearMarkers();
			this.selectedMapDriver = null;
			this.zoomControlAdded = false;
			this.clearTripOverlay();
			this.locationProvider = null;

			// Plain Map first — Operator Fleet Engine tokens are trip-scoped, so Journey Sharing
			// must not initialize with an empty tripId (that blanked the map).
			this.map = new g.maps.Map(mapEl, this.getDefaultMapOptions());

			this.createZoomControl();
			this.plotOnlineDriversOnMap();
		} catch (error) {
			if (this.componentDestroyed) {
				return;
			}
			this.messageService.add({
				severity: 'error',
				summary: 'Map unavailable',
				detail: error instanceof Error ? error.message : 'Google Maps failed to load'
			});
		}
	}

	private async fetchOperatorFleetEngineToken(): Promise<{ token: string; expiresInSeconds: number }> {
		const tripId = this.activeGoogleTripId;
		if (!tripId) {
			throw new Error('No active Google trip for Fleet Engine token');
		}

		const data = await this.googleFleetService.getOperatorFleetEngineToken(
			{
				GoogleTripId: tripId,
				XpressOperatorId: this.appData.authData.OperatorId
			},
			this.messageService
		);

		if (!data?.Token) {
			throw new Error('Fleet Engine token unavailable');
		}

		return { token: data.Token, expiresInSeconds: data.ExpiresInSeconds };
	}

	private setTripOverlay(googleTripId: string | null | undefined): void {
		const tripId = googleTripId?.trim() ?? '';
		if (!tripId) {
			this.clearTripOverlay();
			return;
		}

		void this.ensureJourneySharingForTrip(tripId);
	}

	private clearTripOverlay(): void {
		const journeySharingMounted = !!this.locationProvider;
		this.activeGoogleTripId = '';

		if (this.locationProvider) {
			this.locationProvider.tripId = '';
			this.locationProvider = null;
		}

		if (journeySharingMounted) {
			const mapEl = this.mapElement?.nativeElement;
			const g = (window as unknown as { google?: typeof google }).google;
			if (mapEl && g?.maps?.Map) {
				this.restorePlainMap(g, mapEl);
			}
		}
	}

	private isTripOverlayActive(): boolean {
		return !!this.activeGoogleTripId && !!this.locationProvider;
	}

	/**
	 * Journey Sharing needs a trip-scoped Operator token, so enable it only after
	 * an On Trip pill with GoogleTripId is selected (not at map boot).
	 */
	private async ensureJourneySharingForTrip(tripId: string): Promise<void> {
		if (this.componentDestroyed || !tripId) {
			return;
		}

		this.activeGoogleTripId = tripId;

		if (this.locationProvider) {
			this.locationProvider.tripId = tripId;
			this.plotOnlineDriversOnMap();
			this.focusMapOnSelectedDriver();
			return;
		}

		const mapEl = this.mapElement?.nativeElement;
		const g = (window as unknown as { google?: typeof google }).google;
		if (!mapEl || !g?.maps?.journeySharing) {
			return;
		}

		try {
			this.liveMapMarkers.clearMarkers();
			this.zoomControlAdded = false;
			mapEl.innerHTML = '';

			const locationProvider = new g.maps.journeySharing.FleetEngineTripLocationProvider({
				projectId: OperatorDashboardComponent.FLEET_ENGINE_PROJECT_ID,
				authTokenFetcher: () => this.fetchOperatorFleetEngineToken(),
				pollingIntervalMillis: 5000,
				tripId
			});

			const anticipatedRoutePolylineOptionsSetup: google.maps.journeySharing.PolylineSetupOptions = {
				polylineOptions: {
					strokeOpacity: 1,
					strokeColor: '#004060',
					strokeWeight: 10
				},
				visible: true
			};

			const takenRoutePolylineOptionsSetup: google.maps.journeySharing.PolylineSetupOptions = {
				polylineOptions: {
					strokeOpacity: 1,
					strokeColor: '#38e309',
					strokeWeight: 10
				},
				visible: true
			};

			const mapView = new g.maps.journeySharing.JourneySharingMapView({
				element: mapEl,
				locationProviders: [locationProvider],
				locationProvider: null,
				automaticViewportMode: g.maps.journeySharing.AutomaticViewportMode.FIT_ANTICIPATED_ROUTE,
				anticipatedRoutePolylineSetup: anticipatedRoutePolylineOptionsSetup,
				takenRoutePolylineSetup: takenRoutePolylineOptionsSetup,
				mapOptions: this.getDefaultMapOptions()
			});

			this.locationProvider = locationProvider;
			this.map = mapView.map;
			this.createZoomControl();
			this.plotOnlineDriversOnMap();
			this.focusMapOnSelectedDriver();
		} catch (error) {
			this.locationProvider = null;
			this.activeGoogleTripId = '';
			this.restorePlainMap(g, mapEl);

			if (this.componentDestroyed) {
				return;
			}
			this.messageService.add({
				severity: 'error',
				summary: 'Trip map unavailable',
				detail: error instanceof Error ? error.message : 'Failed to show trip overlay'
			});
		}
	}

	private focusMapOnSelectedDriver(): void {
		if (!this.map || !this.selectedMapDriver) {
			return;
		}

		const lat = Number(this.selectedMapDriver.Lat);
		const lng = Number(this.selectedMapDriver.Long);
		if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
			return;
		}

		this.map.panTo({ lat, lng });
		const zoom = this.map.getZoom();
		if (zoom == null || zoom < 12) {
			this.map.setZoom(13);
		}
	}

	private restorePlainMap(g: typeof google, mapEl: HTMLElement): void {
		if (this.componentDestroyed) {
			return;
		}

		this.liveMapMarkers.clearMarkers();
		this.zoomControlAdded = false;
		mapEl.innerHTML = '';
		this.map = new g.maps.Map(mapEl, this.getDefaultMapOptions());
		this.createZoomControl();
		this.plotOnlineDriversOnMap();
	}

	private plotOnlineDriversOnMap(): void {
		if (!this.map) {
			return;
		}
		if (this.mapPreviewActive) {
			this.applyMapTourMarkers();
			return;
		}

		const drivers = this.onlineDrivers?.Drivers ?? [];
		const tripOverlayActive = this.isTripOverlayActive();
		const hiddenDriverId =
			tripOverlayActive && this.selectedMapDriver ? this.selectedMapDriver.DriverId : null;

		this.liveMapMarkers.setDrivers(drivers, this.map, { hiddenDriverId });
		if (this.selectedMapDriver && !hiddenDriverId) {
			this.liveMapMarkers.setSelectedDriverId(this.selectedMapDriver.DriverId);
		} else if (!this.selectedMapDriver) {
			this.liveMapMarkers.setSelectedDriverId(null);
		}

		// Keep Journey Sharing viewport on the selected trip — don't zoom out to the whole fleet.
		if (!tripOverlayActive) {
			this.fitMapToDriverMarkers();
		}
	}

	ngAfterViewInit(): void {
		this.viewReady = true;
		void this.startMap();
	}

	ngOnInit() {
		this.tourSubscription = this.dashboardTour.replay$.subscribe(() => {
			if (!this.isOperatorPortal) {
				return;
			}
			this.dashboardTour.consumeReplay();
			this.startTour();
		});
		if (this.isOperatorPortal && this.dashboardTour.consumeReplay()) {
			this.startTour();
		} else if (
			this.isOperatorPortal
			&& OperatorDashboardTourService.isFirstLogin()
			&& this.dashboardTour.shouldOffer(this.tourUserId)
		) {
			this.openWelcome();
		}

		this.geoZoneSubscription = this.geoZoneService.selectedGeoZone$.subscribe(value => {
			this.selectGeoZone = value;
			if (this.viewReady) {
				void this.startMap();
			}
		});

		this.markerClickSubscription = this.liveMapMarkers.markerClick$.subscribe(driver => {
			this.openMarkerCard(driver);
		});

		this.getOperatorRiderStatistics();
		this.getOperatorOnlineDrivers();

		this.requestsOptions = {
			indexAxis: 'y',
			maintainAspectRatio: false,
			aspectRatio: 4,
			scales: {
				x: {
					position: 'top',
					stacked: true,
					grid: {
						display: false
					},
					ticks: {
						display: true
					},
					border: {
						display: false
					}
				},
				y: {
					stacked: true,
					grid: {
						display: false
					},
					ticks: {
						display: false
					},
					border: {
						display: false
					}
				}
			},
			plugins: {
				legend: {
					display: true,
					position: 'bottom',
					labels: {
						usePointStyle: true,
						boxWidth: 60,
						padding: 20
					}
				}
			}
		};

		this.options = {
			elements: {
				point: {
					radius: 0,
					hitRadius: 10
				}
			},
			plugins: {
				legend: {
					display: false
				},
				tooltip: {
					enabled: true
				}
			},
			scales: {
				x: {
					grid: {
						display: false
					},
					ticks: {
						display: false
					},
					border: {
						display: false
					}
				},
				y: {
					grid: {
						display: false
					},
					ticks: {
						display: false
					},
					border: {
						display: false
					}
				}
			}
		};
	}

	ngOnDestroy(): void {
		this.componentDestroyed = true;
		this.mapStartGeneration++;
		this.clearMapsLoadTimeout();
		this.clearTripOverlay();
		this.locationProvider = null;
		this.tourSubscription?.unsubscribe();
		this.markerClickSubscription?.unsubscribe();
		this.geoZoneSubscription?.unsubscribe();
		this.liveMapMarkers.clearMarkers();
	}

	getDriverInitials(driver: DriverInformation): string {
		const first = driver.FirstName?.charAt(0) ?? '';
		const last = driver.LastName?.charAt(0) ?? '';
		return `${first}${last}`.toUpperCase();
	}

	getDriverDisplayName(driver: DriverInformation): string {
		return [driver.FirstName, driver.LastName].filter(Boolean).join(' ').trim() || '—';
	}

	getDriverStatusLabel(status: DriverActivityStatus): string {
		return status === DriverActivityStatus.OnTrip ? 'On Trip' : 'Idle';
	}

	isOnTrip(status: DriverActivityStatus): boolean {
		return status === DriverActivityStatus.OnTrip;
	}

	openMarkerCard(driver: DriverInformation): void {
		this.selectedMapDriver = driver;
		this.liveMapMarkers.setSelectedDriverId(driver.DriverId);

		const canShowTrip =
			driver.DriverActivityStatus === DriverActivityStatus.OnTrip && !!driver.GoogleTripId;
		this.setTripOverlay(canShowTrip ? driver.GoogleTripId : null);
	}

	closeMarkerCard(): void {
		this.selectedMapDriver = null;
		this.liveMapMarkers.setSelectedDriverId(null);
		this.clearTripOverlay();
	}

	getOperatorRevenue() {
		this.isRevenueLoading = true;
		const request: GetOperatorRevenueRequest = {
			XpressOperatorId: this.appData.authData.OperatorId,
			StartDate: moment(this.filterObj.startDate).format('YYYY-MM-DDTHH:mm:ss'),
			EndDate: moment(this.filterObj.endDate).format('YYYY-MM-DDTHH:mm:ss'),
			TimeZoneOffset: 8
		};
		this.operatorDashboardService
			.getOperatorRevenue(request, this.messageService)
			.then(response => {
				this.revenue = response ?? {
					TotalRevenue: 0,
					PercentageIncrease: 0,
					ChartLabels: [],
					LabelsData: [],
					Success: false
				};
				this.revenueData = {
					labels: this.revenue.ChartLabels ? this.revenue.ChartLabels : [],
					datasets: [
						{
							label: 'Revenue',
							data: this.revenue.LabelsData ? this.revenue.LabelsData : [],
							fill: true,
							borderCapStyle: 'round',
							tension: 0.4,
							borderColor: '#165DFF',
							backgroundColor: 'rgba(22, 93, 255, 0.2)'
						}
					]
				};
			})
			.catch(() => {
				this.revenue = {
					TotalRevenue: 0,
					PercentageIncrease: 0,
					ChartLabels: [],
					LabelsData: [],
					Success: false
				};
				this.revenueData = { labels: [], datasets: [] };
			})
			.finally(() => {
				this.isRevenueLoading = false;
			});
	}

	hasBookingActivity(): boolean {
		return (
			(this.statistics?.TotalCompletedDeliveryOrder ?? 0) > 0 ||
			(this.statistics?.TotalOrderRequesting ?? 0) > 0 ||
			(this.statistics?.TotalActiveDeliveryOrder ?? 0) > 0
		);
	}

	isKpiEmpty(card: OperatorInfoCard): boolean {
		const footer = (card?.Footer || '').replace(/,/g, '').trim();
		const match = footer.match(/(\d+)\s+out of\s+(\d+)/i);
		if (match) {
			return Number(match[2]) === 0;
		}
		const hasSeries = (card?.LabelsData || []).some(value => Number(value) > 0);
		return Number(card?.HeaderPercentage || 0) === 0 && !hasSeries;
	}

	kpiEmptyFooter(): string {
		if (this.performancePreviewActive && !this.tourPreviewFilled) {
			return 'No bookings yet in this period';
		}
		return this.hasBookingActivity()
			? 'No rate data in this period'
			: 'No bookings yet in this period';
	}

	hasKpiChart(card: OperatorInfoCard): boolean {
		return !this.isKpiEmpty(card) && !!card?.ChartLabels?.length;
	}

	kpiTestId(card: OperatorInfoCard): string {
		return (card?.Header || 'kpi').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
	}

	getOperatorOnlineDrivers() {
		this.isOnlineDriverLoading = true;
		this.isOnlineDriverError = false;
		const request: GetOperatorOnlineDriversRequest = {
			XpressOperatorId: this.appData.authData.OperatorId
		};
		this.operatorDashboardService
			.getOperatorOnlineDrivers(request, this.messageService)
			.then(response => {
				this.onlineDrivers = response ?? new GetOperatorOnlineDriversResponse();
				if (!this.onlineDrivers.Drivers) {
					this.onlineDrivers.Drivers = [];
				}
				this.isOnlineDriverLoading = false;
				if (!this.map) {
					return;
				}

				const selectedDriver = this.selectedMapDriver;
				if (selectedDriver) {
					const refreshed = this.onlineDrivers.Drivers.find(
						d => d.DriverId === selectedDriver.DriverId
					);
					if (!refreshed) {
						this.closeMarkerCard();
					} else {
						this.selectedMapDriver = refreshed;
						const canShowTrip =
							refreshed.DriverActivityStatus === DriverActivityStatus.OnTrip &&
							!!refreshed.GoogleTripId;
						this.setTripOverlay(canShowTrip ? refreshed.GoogleTripId : null);
					}
				}

				this.plotOnlineDriversOnMap();
			})
			.catch(error => {
				this.isOnlineDriverError = true;
				this.isOnlineDriverLoading = false;
				this.onlineDrivers = new GetOperatorOnlineDriversResponse();
				this.closeMarkerCard();
				if (this.map) {
					this.liveMapMarkers.setDrivers([], this.map, { hiddenDriverId: null });
				} else {
					this.liveMapMarkers.clearMarkers();
				}
				this.messageService.add({
					severity: 'error',
					summary: 'Cannot get online drivers',
					detail: error
				});
			});
	}

	private fitMapToDriverMarkers(): void {
		if (!this.map) {
			return;
		}

		const positions = this.liveMapMarkers.getVisibleMarkerPositions();
		if (positions.length === 0) {
			return;
		}

		if (positions.length === 1) {
			this.map.setCenter(positions[0]);
			this.map.setZoom(15);
			return;
		}

		const bounds = new google.maps.LatLngBounds();
		for (const position of positions) {
			bounds.extend(position);
		}
		this.map.fitBounds(bounds, 48);
	}

	getOperatorRiderStatistics() {
		this.isStatisticsLoading = true;

		const request: GetOperatorDashboardStatisticsRequest = {
			OperatorId: this.appData.authData.OperatorId,
			StartDateJoin: moment(this.filterObj.startDate).format('YYYY-MM-DDTHH:mm:ss'),
			EndDateJoin: moment(this.filterObj.endDate).format('YYYY-MM-DDTHH:mm:ss'),
			TimeZoneOffset: 8
		};

		this.operatorDashboardService
			.getOperatorRiderStatistics(request, this.messageService)
			.then(response => {
				if (!response) {
					this.applyStatisticsFailure('Failed to load dashboard statistics');
					return;
				}
				this.statistics = response;
				if (!response.Success) {
					this.messageService.add({ severity: 'error', summary: 'Error', detail: response.ErrorMessage });
					this.requestsData = { labels: [], datasets: [] };
					return;
				}
				const datasets = [];
				const backGroundColor = ['#FFC043', '#0A84FF', '#F77F00'];
				const longChart = this.statistics.LongChart;
				for (let index = 0; index < longChart?.OperatorInfoCards?.length; index++) {
					const value = +longChart.OperatorInfoCards[index].HeaderPercentage;
					datasets.push({
						label: `${longChart.OperatorInfoCards[index].Header}`,
						data: [value],
						backgroundColor: backGroundColor[index],
						borderRadius: 4
					});
				}
				this.requestsData = {
					labels: ['Selected Period'],
					datasets
				};
			})
			.catch(err => {
				this.applyStatisticsFailure(err?.ErrorMessage ?? 'Failed to load dashboard statistics');
			})
			.finally(() => {
				this.isStatisticsLoading = false;
			});
	}

	private applyStatisticsFailure(errorMessage: string): void {
		this.statistics = {
			OperatorInfoCards: [],
			TotalRecords: 0,
			TotalRiderRecords: 0,
			TotalCompletedDeliveryOrder: 0,
			TotalOrderRequesting: 0,
			TotalActiveDeliveryOrder: 0,
			LongChart: { OperatorInfoCards: [], ChartLabels: [], LabelsData: [] },
			Success: false,
			ErrorMessage: errorMessage,
			ErrorCode: 0
		};
		this.requestsData = { labels: [], datasets: [] };
		this.messageService.add({
			severity: 'error',
			summary: 'Error',
			detail: errorMessage
		});
	}

	public createZoomControl() {
		if (this.zoomControlAdded || !this.map) {
			return;
		}

		const controlDiv = document.createElement('div');
		controlDiv.style.padding = '15px';

		const controlWrapper = document.createElement('div');
		controlWrapper.style.cursor = 'pointer';
		controlWrapper.style.textAlign = 'center';
		controlWrapper.style.backgroundColor = '#004060';
		controlWrapper.style.borderRadius = '6px';
		controlDiv.appendChild(controlWrapper);

		const zoomInButton = document.createElement('div');
		zoomInButton.style.width = '38px';
		zoomInButton.style.height = '38px';
		zoomInButton.style.backgroundImage = 'url("XpressApp/assets/images/map-control/plus-button.svg")';
		controlWrapper.appendChild(zoomInButton);

		const zoomOutButton = document.createElement('div');
		zoomOutButton.style.width = '38px';
		zoomOutButton.style.height = '38px';
		zoomOutButton.style.backgroundImage = 'url("XpressApp/assets/images/map-control/minus-button.svg")';
		controlWrapper.appendChild(zoomOutButton);

		google.maps.event.addDomListener(zoomInButton, 'click', () => {
			this.map.setZoom((this.map.getZoom() ?? 5) + 1);
		});

		google.maps.event.addDomListener(zoomOutButton, 'click', () => {
			this.map.setZoom((this.map.getZoom() ?? 5) - 1);
		});

		this.map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(controlDiv);
		this.zoomControlAdded = true;
	}

	onDatePickerApply(selection: DashboardDateSelection): void {
		this.datePreset = selection.preset;
		this.filterObj.startDate = selection.startDate;
		this.filterObj.endDate = selection.endDate;
		this.getOperatorRiderStatistics();
	}

	handleViewDriverInfo(driverId: number) {
		if (driverId < 0) {
			return;
		}
		if (this.router.url.includes('/operator/')) {
			this.router.navigate(['/operator/dashboard/drivers'], {
				queryParams: { driverId }
			});
			return;
		}

		sessionStorage.setItem('driverId', `${driverId}`);
		this.OnViewDriverClick.emit({
			root: 3,
			menuFlag: 32,
			label: 'Drivers',
			id: 'back-office-operator-drivers'
		});
	}
}
